import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Eye, EyeOff, RefreshCw, Save, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';
import { platformService } from '@/services/platform';
import { type AgentConfigUpdate, agentService } from '@/services/agent';
import { NotificationSettings } from '@/components/settings/NotificationSettings';
import { RiskConfigForm } from '@/components/settings/RiskConfigForm';

export default function Settings() {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Platform credentials state
  const [credentials, setCredentials] = useState({
    apiKey: '',
    apiSecret: '',
    isPaper: true,
  });

  // Agent settings state
  const [agentSettings, setAgentSettings] = useState({
    strategy: 'momentum',
    riskTolerance: 'medium',
    maxPositionSize: 25,
    stopLossPercent: 5,
    takeProfitPercent: 10,
    maxDailyTrades: 20,
    maxDailyLoss: 5000,
  });

  // Load agent config from backend
  const { data: agentConfig } = useQuery({
    queryKey: ['agent', 'config'],
    queryFn: () => agentService.getConfig(),
  });

  // Sync local state when backend config is loaded
  useEffect(() => {
    if (agentConfig) {
      setAgentSettings({
        strategy: agentConfig.strategy,
        riskTolerance: agentConfig.riskTolerance,
        maxPositionSize: agentConfig.maxPositionSizePct,
        stopLossPercent: agentConfig.stopLossPct,
        takeProfitPercent: agentConfig.takeProfitPct,
        maxDailyTrades: agentConfig.maxDailyTrades,
        maxDailyLoss: agentConfig.maxDailyLoss,
      });
    }
  }, [agentConfig]);

  // Platform status query
  const { data: platformStatus } = useQuery({
    queryKey: ['platform', 'status'],
    queryFn: () => platformService.getStatus(),
  });

  // Platform credentials mutation
  const updateCredentialsMutation = useMutation({
    mutationFn: (creds: { apiKey: string; apiSecret: string; isPaper: boolean }) =>
      platformService.updateCredentials(creds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform'] });
      setCredentials((prev) => ({ ...prev, apiKey: '', apiSecret: '' }));
      toast({ title: 'Saved', description: 'Platform credentials saved', variant: 'success' });
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: () => platformService.testConnection(),
  });

  const handleSettingChange = (key: string, value: string | number) => {
    setAgentSettings((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  // Save agent settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const updates: AgentConfigUpdate = {
        strategy: agentSettings.strategy,
        risk_tolerance: agentSettings.riskTolerance,
        max_position_size_pct: agentSettings.maxPositionSize,
        stop_loss_pct: agentSettings.stopLossPercent,
        take_profit_pct: agentSettings.takeProfitPercent,
        max_daily_trades: agentSettings.maxDailyTrades,
        max_daily_loss: agentSettings.maxDailyLoss,
      };
      return agentService.updateConfig(updates);
    },
    onSuccess: () => {
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['agent', 'config'] });
    },
  });

  const handleSave = () => {
    saveSettingsMutation.mutate();
  };

  const handleReset = () => {
    if (agentConfig) {
      setAgentSettings({
        strategy: agentConfig.strategy,
        riskTolerance: agentConfig.riskTolerance,
        maxPositionSize: agentConfig.maxPositionSizePct,
        stopLossPercent: agentConfig.stopLossPct,
        takeProfitPercent: agentConfig.takeProfitPct,
        maxDailyTrades: agentConfig.maxDailyTrades,
        maxDailyLoss: agentConfig.maxDailyLoss,
      });
    }
    setIsDirty(false);
  };

  const handleCredentialsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateCredentialsMutation.mutate(credentials);
  };

  const NumberSettingField = ({
    label,
    value,
    min,
    max,
    step,
    description,
    settingKey,
  }: {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    description: string;
    settingKey: string;
  }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          const val = parseFloat(e.target.value);
          handleSettingChange(settingKey, isNaN(val) ? 0 : val);
        }}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      />
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">Configure your trading platform and preferences.</p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleReset}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button onClick={handleSave} disabled={saveSettingsMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {saveSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Platform Connection */}
        <Card>
          <CardHeader>
            <CardTitle>Platform Connection</CardTitle>
            <CardDescription>Connect to your Alpaca paper trading account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">API Key</label>
                <Input
                  type="text"
                  value={credentials.apiKey}
                  onChange={(e) => setCredentials((prev) => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Enter your Alpaca API Key"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">API Secret</label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    value={credentials.apiSecret}
                    onChange={(e) =>
                      setCredentials((prev) => ({ ...prev, apiSecret: e.target.value }))
                    }
                    placeholder="Enter your Alpaca API Secret"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPaper"
                  checked={credentials.isPaper}
                  onChange={(e) =>
                    setCredentials((prev) => ({ ...prev, isPaper: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-border"
                />
                <label htmlFor="isPaper" className="text-sm">
                  Paper Trading Mode
                </label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={updateCredentialsMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Credentials
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testConnectionMutation.mutate()}
                  disabled={testConnectionMutation.isPending}
                >
                  Test Connection
                </Button>
              </div>
              {testConnectionMutation.data && (
                <div
                  className={`flex items-center gap-2 ${testConnectionMutation.data.valid ? 'text-primary' : 'text-destructive'}`}
                >
                  {testConnectionMutation.data.valid ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  {testConnectionMutation.data.valid
                    ? 'Connection successful!'
                    : testConnectionMutation.data.error || 'Connection failed'}
                </div>
              )}
            </form>

            {platformStatus && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-medium mb-2">Current Status</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Status: {platformStatus.status}</p>
                  <p>Platform: {platformStatus.platform || 'Alpaca'}</p>
                  {platformStatus.lastSync && (
                    <p>Last Sync: {new Date(platformStatus.lastSync).toLocaleString()}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Settings */}
        <div className="rounded-lg border bg-card">
          <div className="border-b p-4">
            <h2 className="font-semibold">Account</h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Username</label>
              <input
                type="text"
                value={user?.username || 'demo'}
                disabled
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={user?.email || 'demo@example.com'}
                disabled
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              />
            </div>
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={() => alert('Change password feature is coming soon.')}
              >
                Change Password
              </Button>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <NotificationSettings />

        {/* Portfolio Settings */}
        <div className="rounded-lg border bg-card">
          <div className="border-b p-4">
            <h2 className="font-semibold">Portfolio</h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <p className="font-medium">Starting Capital</p>
                <p className="text-sm text-muted-foreground">Initial paper trading balance</p>
              </div>
              <p className="text-xl font-bold">{formatCurrency(100000)}</p>
            </div>
            <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-700 dark:text-yellow-400">
                    Reset Portfolio
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This will reset your portfolio to the starting capital and clear all positions
                    and trade history.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => alert('Reset portfolio feature is coming soon.')}
                  >
                    Reset Portfolio
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Agent Settings */}
        <div className="lg:col-span-2 rounded-lg border bg-card">
          <div className="border-b p-4">
            <h2 className="font-semibold">AI Trading Agent Configuration</h2>
          </div>
          <div className="p-4">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Strategy */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Trading Strategy</label>
                <select
                  value={agentSettings.strategy}
                  onChange={(e) => handleSettingChange('strategy', e.target.value)}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  <option value="momentum">Momentum</option>
                  <option value="mean_reversion">Mean Reversion</option>
                  <option value="trend_following">Trend Following</option>
                  <option value="ml_based">ML-Based</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  The strategy the agent uses for trading decisions
                </p>
              </div>

              {/* Risk Tolerance */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Risk Tolerance</label>
                <select
                  value={agentSettings.riskTolerance}
                  onChange={(e) => handleSettingChange('riskTolerance', e.target.value)}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Affects position sizing and trade frequency
                </p>
              </div>

              <NumberSettingField
                label="Max Position Size (%)"
                value={agentSettings.maxPositionSize}
                min={1}
                max={100}
                description="Maximum % of portfolio in a single position"
                settingKey="maxPositionSize"
              />
              <NumberSettingField
                label="Stop Loss (%)"
                value={agentSettings.stopLossPercent}
                min={1}
                max={50}
                description="Automatic sell trigger for losses"
                settingKey="stopLossPercent"
              />
              <NumberSettingField
                label="Take Profit (%)"
                value={agentSettings.takeProfitPercent}
                min={1}
                max={100}
                description="Automatic sell trigger for profits"
                settingKey="takeProfitPercent"
              />
              <NumberSettingField
                label="Max Daily Trades"
                value={agentSettings.maxDailyTrades}
                min={1}
                max={100}
                description="Maximum trades the agent can execute per day"
                settingKey="maxDailyTrades"
              />
            </div>

            {/* Agent config save controls */}
            <div className="flex items-center gap-2 pt-4 border-t mt-6">
              <Button
                onClick={handleSave}
                disabled={saveSettingsMutation.isPending || !isDirty}
              >
                <Save className="h-4 w-4 mr-2" />
                {saveSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={handleReset} disabled={!isDirty}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
            {saveSettingsMutation.isSuccess && (
              <p className="text-primary text-sm mt-2">Agent configuration saved successfully!</p>
            )}
            {saveSettingsMutation.isError && (
              <p className="text-destructive text-sm mt-2">Failed to save agent configuration</p>
            )}
          </div>
        </div>
      </div>

      {/* Risk Configuration Section */}
      <div className="border-t pt-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Risk & Budget Controls</h2>
          <p className="text-muted-foreground text-sm">
            Configure trading limits and risk parameters
          </p>
        </div>
        <RiskConfigForm />
      </div>
    </div>
  );
}
